<template>
    <v-dialog
        :value="isOpen"
        max-width="600px"
        transition="slide-y-transition"
        eager
        persistent
        @keydown.esc="onCancel"
        @click:outside="onCancel"
    >  
        <AssignProductionPropertiesDialog
            :is-open="assignProductionPropertiesDialog"
            :component="component"
            :properties="properties"
            @on-save="saveProductionProperties"
            @on-close="closeAssignProductionPropertiesDialog"
        />
        <AssignProductionProcessDialog
            :is-open="assignProductionProcessDialog"
            :component="component"
            :processes="processes"
            @on-save="saveProductionProcess"
            @on-close="closeProductionProcessDialog"
        />
        <AssignProductionPropertyAlgorithmDialog
            :is-open="assignProductionPropertyAlgorithmDialog"
            :component="component"
            :property="currentProductionProperty"
            @on-save="saveProductionPropertyAlgorithm"
            @on-close="closeAssignProductionPropertyAlgorithmDialog"
        />
        <ConfirmDialog
            :is-open="isConfirmCancelDialog"
            @on-confirm="confirmCancelDialog"
            @on-abort="closeCancelDialog"
        >
            <template #title>
                Usuwanie definicji cechy produkcyjnej
            </template>
            <template #content>
                Czy na pewno chcesz usunąć tę definicję?
            </template>
            <template #abortButton>
                Nie
            </template>
        </ConfirmDialog>
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    Wybierz parametry i procesy do dodania
                </h1>
            </v-card-title>
            <v-card-text>
                <v-col>
                    <v-row dense>
                        <v-col cols="12" lg="6">
                            <v-row dense align="center">
                                <v-col sm="4" class="text-no-wrap">
                                    <span class="text-subtitle-2">Parametry:</span>
                                </v-col>
                                <v-col sm="8">
                                    <v-container fluid class="pa-0">
                                        <v-row justify="end" dense>
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        color="primary"
                                                        medium
                                                        class="mx-1"
                                                        text
                                                        data-sentry-label="Dodaj parametry"
                                                        v-on="on"
                                                        @click="showAssignProductionPropertiesDialog()"
                                                    >
                                                        <v-icon color="blue">
                                                            mdi-plus
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Dodaj parametry</span>
                                            </v-tooltip>
                                        </v-row>
                                    </v-container>
                                </v-col>
                                <v-alert
                                    v-if="component.productTypeComponentProperties.length === 0"
                                    type="info"
                                    text
                                    border="left"
                                >
                                    Brak przypisanych cech produkcyjnych.
                                </v-alert>
                                <v-row v-else>
                                    <v-col>
                                        <v-chip
                                            v-for="(productionProperty, productionPropertyIndex) in component.productTypeComponentProperties"
                                            :key="productionProperty.productProductionProperty.id"
                                            close
                                            :color="productionProperty.algorithm ? 'primary' : undefined"
                                            :dark="productionProperty.algorithm ? true : false"
                                            class="ma-1"
                                            @update:active="removeProductionProperty(productionProperty)"
                                        >
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-icon
                                                        v-if="productionProperty.productProductionProperty.isAlgorithmic"
                                                        left
                                                        :color="productionProperty.algorithm ? undefined : 'primary'"
                                                        v-on="on"
                                                        @click="showAssignProductionPropertyAlgorithmDialog(productionProperty, productionPropertyIndex)"
                                                    >
                                                        mdi-cog
                                                    </v-icon>
                                                </template>
                                                <span>Ustawienie algorytmu</span>
                                            </v-tooltip>
                                            {{ productionProperty.productProductionProperty.verboseName }}
                                        </v-chip>
                                    </v-col>
                                </v-row>
                            </v-row>
                        </v-col>
                        <v-col cols="12" lg="6">
                            <v-row dense align="center">
                                <v-col sm="4" class="text-no-wrap">
                                    <span class="text-subtitle-2">Procesy:</span>
                                </v-col>
                                <v-col sm="8">
                                    <v-container fluid class="pa-0">
                                        <v-row justify="end" dense>
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        color="primary"
                                                        medium
                                                        class="mx-1"
                                                        text
                                                        data-sentry-label="Dodaj procesy"
                                                        v-on="on"
                                                        @click="showAssignProductionProcessDialog()"
                                                    >
                                                        <v-icon color="blue">
                                                            mdi-plus
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Dodaj procesy</span>
                                            </v-tooltip>
                                        </v-row>
                                    </v-container>
                                </v-col>
                                <v-alert
                                    v-if="component.productTypeComponentProcesses.length === 0"
                                    type="info"
                                    text
                                    border="left"
                                >
                                    Brak przypisanych procesów produkcyjnych.
                                </v-alert>
                                <v-row v-else>
                                    <v-col>
                                        <v-chip
                                            v-for="productionProcess in component.productTypeComponentProcesses"
                                            :key="productionProcess.productionProcess.id"
                                            close
                                            class="ma-1"
                                            @update:active="removeProductionProcess(productionProcess)"
                                        >
                                            {{ productionProcess.productionProcess.name }}
                                        </v-chip>
                                    </v-col>
                                </v-row>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-col>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    @click="onCancel"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Dodaj"
                    @click="onSave"
                >
                    Dodaj
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { isEqual } from 'lodash'
import AssignProductionProcessDialog from '@/views/Configurations/Common/AssignProductionProcessDialog'
import AssignProductionPropertiesDialog from '@/views/Configurations/Common/AssignProductionPropertiesDialog'
import AssignProductionPropertyAlgorithmDialog from '@/views/Configurations/Common/AssignProductionPropertyAlgorithmDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import tooltipDelay from '@/mixins/tooltip'

export default {
    name: 'AssignProcesesAndParametresDialog',
    components: {
        AssignProductionPropertyAlgorithmDialog,
        AssignProductionPropertiesDialog,
        AssignProductionProcessDialog,
        ConfirmDialog,
    },
    mixins: [
        tooltipDelay,
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        component: {
            type: Object,
            default: null
        },
        properties: {
            type: Array,
            default: () => []
        },
        processes: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            initialState: {
                processes: [],
                properties: [],
            },
            isConfirmCancelDialog: false,
            productProductionProperty: {
                name: null
            },
            currentProductionProperty: {
                index: null,
                productProductionProperty: {
                    name: null
                }
            },
            assignProductionPropertyAlgorithmDialog: false,
            assignProductionPropertiesDialog: false,
            assignProductionProcessDialog: false,
        }
    },
    computed: {
        hasChanges() {
            const hasChanges = !isEqual(
                this.initialState.processes,
                this.component.productTypeComponentProcesses
            ) || !isEqual(
                this.initialState.properties,
                this.component.productTypeComponentProperties
            )

            return hasChanges
        },
    },
    watch: {
        isOpen(value) {
            if (value === true) {
                this.initialState = {
                    processes: Object.assign([], this.component.productTypeComponentProcesses),
                    properties: Object.assign([], this.component.productTypeComponentProperties),
                }
            } else {
                this.initialState = {
                    processes: [],
                    properties: [],
                }
            }
        }
    },
    methods: {
        onSave() {
            this.$emit('on-save')
            this.onClose()
        },
        onClose() {
            this.$emit('on-close')
        },
        onCancel() {            
            if (this.hasChanges) {
                this.isConfirmCancelDialog = true
            } else {
                this.onClose()
            }
        },
        confirmCancelDialog() {
            this.component.productTypeComponentProcesses
                = Object.assign([], this.initialState.processes)
            this.component.productTypeComponentProperties
                = Object.assign([], this.initialState.properties)
            this.closeCancelDialog()
            this.onClose()
        },
        closeCancelDialog() {
            this.isConfirmCancelDialog = false
        },
        showAssignProductionProcessDialog() {
            this.assignProductionProcessDialog = true
        },
        closeProductionProcessDialog() {
            this.assignProductionProcessDialog = false
            this.clearCurrentProductionProperty()
        },
        saveProductionProcess(selectedProcesses) {
            selectedProcesses.forEach(item => {
                this.component.productTypeComponentProcesses.push({
                    productionProcessId: item.id,
                    productionProcess: item
                })
            })
            this.assignProductionProcessDialog = false
        },
        removeProductionProcess(productionProcess) {
            this.component.productTypeComponentProcesses
                .splice(this.component.productTypeComponentProcesses.indexOf(productionProcess), 1)
        },
        showAssignProductionPropertyAlgorithmDialog(productionProperty, productionPropertyIndex) {
            this.assignProductionPropertyAlgorithmDialog = true
            this.currentProductionProperty = productionProperty
            this.currentProductionProperty.index = productionPropertyIndex
        },
        closeAssignProductionPropertyAlgorithmDialog() {
            this.assignProductionPropertyAlgorithmDialog = false
            this.clearCurrentProductionProperty()
        },
        saveProductionPropertyAlgorithm(propertyIndex, algorithm, usedProductionProperties) {
            this.component.productTypeComponentProperties[propertyIndex].algorithm = algorithm
            this.component.productTypeComponentProperties[propertyIndex].usedProductionPropertyId = usedProductionProperties
        },
        showAssignProductionPropertiesDialog() {
            this.assignProductionPropertiesDialog = true
        },
        closeAssignProductionPropertiesDialog() {
            this.assignProductionPropertiesDialog = false
        },
        saveProductionProperties(selectedProperties) {
            selectedProperties.forEach(item => {
                this.component.productTypeComponentProperties.push({
                    productProductionPropertyId: item.id,
                    productProductionProperty: item
                })
            })
        },
        removeProductionProperty(productionProperty) {
            let isProductionPropertyUsed

            this.component.productTypeComponentProperties.forEach(property => {
                if (property.usedProductionPropertyId) {
                    isProductionPropertyUsed = property.usedProductionPropertyId.some(item => {
                        return item === productionProperty.productProductionPropertyId
                    })
                }
            })
            if (isProductionPropertyUsed) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Nie można usunąć cechy użytej w algorytmie.'
                })

                return
            }
            this.component.productTypeComponentProperties
                .splice(this.component.productTypeComponentProperties.indexOf(productionProperty), 1)
        },
        clearCurrentProductionProperty() {
            this.currentProductionProperty = {
                productProductionProperty: {
                    name: null
                }
            }
        },
    }
}
</script>

